import moment from 'moment'

class ImgAddMark {
  constructor () {
    this.markInfo = {
      address: '',
      time: ''
    }
  }

  // 获取视频第二帧图片
  getVideoFirstFrame (videoFile) {
    return new Promise((resolve, reject) => {
      // 检查Canvas支持
      if (!this.isCanvasSupported()) {
        reject(new Error('浏览器不支持Canvas'))
        return
      }

      const video = document.createElement('video')
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      let timeoutId = null

      // 设置超时处理
      const timeout = setTimeout(() => {
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
        reject(new Error('视频加载超时'))
      }, 100000) // 100秒超时

      const cleanup = () => {
        clearTimeout(timeout)
        if (video.src) {
          URL.revokeObjectURL(video.src)
        }
      }

      video.onerror = () => {
        cleanup()
        reject(new Error('视频加载失败'))
      }

      video.onloadeddata = () => {
        // 设置视频时间到第二帧（大约0.1秒）
        video.currentTime = 0.1
        video.onseeked = () => {
          try {
            // 设置canvas尺寸为视频尺寸
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight

            // 将视频第二帧绘制到canvas
            ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight)

            // 转换为blob
            canvas.toBlob((blob) => {
              cleanup()
              if (blob) {
                // 创建新的File对象，保持原始文件名但改为图片格式
                const fileName = videoFile.name.replace(/\.[^/.]+$/, '.jpg')
                const firstFrameFile = new File([blob], fileName, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                })
                resolve(firstFrameFile)
              } else {
                reject(new Error('Canvas转Blob失败'))
              }
            }, 'image/jpeg', 0.9)
          } catch (error) {
            cleanup()
            reject(new Error('获取视频第二帧失败: ' + error.message))
          }
        }
      }

      // 设置视频源
      video.src = URL.createObjectURL(videoFile)
      video.muted = true
      video.playsInline = true
      video.preload = 'metadata'

      // 加载视频
      video.load()
    })
  }

  // 添加水印到图片
  async addWatermarkToImage (imageFile, imageUrl, watermarkTime) {
    return new Promise(async (resolve, reject) => {
      // 检查Canvas支持
      if (!this.isCanvasSupported()) {
        reject(new Error('浏览器不支持Canvas'))
        return
      }
      // 加载自定义字体
      try {
        await this.loadCustomFont()
      } catch (error) {
        console.warn('字体加载失败，使用默认字体:', error)
      }
      const img = new Image()
      img.onerror = () => {
        reject(new Error('图片加载失败'))
      }
      img.onload = async () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        // 设置canvas尺寸
        canvas.width = img.width
        canvas.height = img.height
        // 绘制原始图片
        ctx.drawImage(img, 0, 0, img.width, img.height)
        // 获取水印文字
        if (!this.markInfo.address) {
          this.markInfo = await this.getmarkInfo(imageUrl)
          // 无水印的数据
          if (!this.markInfo.address) {
            resolve(imageFile)
            return
          }
        }
        const locationInfo = this.markInfo.address
        let currentTime = this.getWaterMarkTime(2)
        if (watermarkTime) {
          currentTime = watermarkTime
        }
        // 计算尺寸
        const baseSize = Math.min(img.width, img.height)
        const maxWidth = baseSize * 2.0 / 3.0
        const textSize = baseSize * 0.04
        const padding = textSize * 0.3
        const marginLeft = baseSize * 0.03
        const marginBottom = baseSize * 0.03
        const borderRadius = textSize * 0.25
        // 设置字体
        const font = `${textSize}px "SourceHanSansCN-Normal"`
        ctx.font = font
        ctx.fillStyle = '#ffffff'
        // 计算位置信息的换行
        const locationLines = this.wrapText(locationInfo, font, maxWidth)
        // 计算时间宽度
        const timeSize = ctx.measureText(currentTime)
        // 计算最大行宽度
        let maxLineWidth = 0
        for (const line of locationLines) {
          const lineSize = ctx.measureText(line)
          maxLineWidth = Math.max(maxLineWidth, lineSize.width)
        }
        maxLineWidth = Math.max(maxLineWidth, timeSize.width)
        // 计算行高和行间距
        const lineHeight = textSize
        const lineSpacing = lineHeight * 0.3
        // 计算背景矩形尺寸
        const lineCount = locationLines.length
        const rectWidth = maxLineWidth + padding * 2
        const rectHeight = (lineHeight * (lineCount + 1) + lineSpacing * lineCount) + padding * 2
        // 背景矩形位置 - 左下角
        const backgroundRect = {
          x: marginLeft,
          y: img.height - rectHeight - marginBottom,
          width: rectWidth,
          height: rectHeight
        }
        // 绘制圆角背景
        ctx.save()
        ctx.beginPath()
        // 使用传统方法绘制圆角矩形
        const x = backgroundRect.x
        const y = backgroundRect.y
        const width = backgroundRect.width
        const height = backgroundRect.height
        const radius = borderRadius
        ctx.moveTo(x + radius, y)
        ctx.lineTo(x + width - radius, y)
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
        ctx.lineTo(x + width, y + height - radius)
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
        ctx.lineTo(x + radius, y + height)
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
        ctx.lineTo(x, y + radius)
        ctx.quadraticCurveTo(x, y, x + radius, y)
        ctx.closePath()
        ctx.fillStyle = 'rgba(170, 170, 170, 0.5)'
        ctx.fill()
        ctx.restore()
        // 绘制文字
        let textX = backgroundRect.x + padding
        let textY = backgroundRect.y + padding + textSize
        // 绘制位置信息
        for (const line of locationLines) {
          ctx.fillText(line, textX, textY)
          textY += lineHeight + lineSpacing
        }
        // 绘制时间信息
        ctx.fillText(currentTime, textX, textY)
        // 转换为blob
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Canvas转Blob失败'))
          }
        }, imageFile.type, 0.9)
      }
      img.src = URL.createObjectURL(imageFile)
    })
  }

  // 获取水印信息（地址和时间）
  async getmarkInfo (imgUrl) {
    try {
      // 从指定URL获取地址和时间信息
      const url = filter.ossHost(imgUrl.substring(0, imgUrl.lastIndexOf('/')) + '/redundancy.json')
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      if (response.ok) {
        const data = await response.json()
        // 时间加 1 分钟
        const timestamp = (filter.toUnix(data.time) * 1000) + 60000
        // 更新 markInfo.time 为新的时间戳
        const time = moment(timestamp).format('YYYY-MM-DD HH:mm:ss')
        return {
          address: data.address || '未知位置',
          time: time || ''
        }
      } else {
        console.error('获取水印信息失败，HTTP状态:', response.status)
        return {
          address: '',
          time: ''
        }
      }
    } catch (error) {
      console.error('获取水印信息失败:', error)
      return {
        address: '',
        time: ''
      }
    }
  }

  // 检查是否支持Canvas
  isCanvasSupported () {
    const canvas = document.createElement('canvas')
    return !!(canvas.getContext && canvas.getContext('2d'))
  }

  // type 1: 返回时间戳, 2: 返回标准时间格式
  getWaterMarkTime (type) {
    // 获取时间戳并加2秒
    const timestamp = (filter.toUnix(this.markInfo.time) * 1000) + Math.floor(Math.random() * 30001)
    // 更新 markInfo.time 为新的时间戳
    this.markInfo.time = moment(timestamp).format('YYYY-MM-DD HH:mm:ss')
    // 根据type返回不同格式
    if (type === 1) {
      // 返回时间戳
      return timestamp
    } else {
      // 返回标准时间格式 yyyy-MM-dd HH:mm:ss
      return moment(timestamp).format('YYYY-MM-DD HH:mm:ss')
    }
  }

  // 文字换行处理
  wrapText (text, font, maxWidth) {
    if (!text || text.length === 0) {
      return []
    }
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    ctx.font = font
    const lines = []
    let currentLine = ''
    for (let i = 0; i < text.length; i++) {
      const currentChar = text.charAt(i)
      const testLine = currentLine + currentChar
      const testSize = ctx.measureText(testLine)
      if (testSize.width <= maxWidth) {
        currentLine += currentChar
      } else {
        if (currentLine.length > 0) {
          lines.push(currentLine)
          currentLine = currentChar
        } else {
          // 单个字符就超出宽度，强制添加
          lines.push(currentChar)
        }
      }
    }
    if (currentLine.length > 0) {
      lines.push(currentLine)
    }
    return lines
  }

  // 加载自定义字体
  loadCustomFont () {
    return new Promise((resolve, reject) => {
      // 创建字体样式
      const style = document.createElement('style')
      style.textContent = `
        @font-face {
          font-family: 'SourceHanSansCN-Normal';
          src: url('/static/fonts/SourceHanSansCN-Normal.otf') format('opentype');
          font-display: swap;
        }
      `
      document.head.appendChild(style)
      // 等待字体加载完成
      if (document.fonts) {
        // 等待字体加载完成
        const fontFace = new FontFace('SourceHanSansCN-Normal', 'url(/static/fonts/SourceHanSansCN-Normal.otf)')
        fontFace.load().then(() => {
          document.fonts.add(fontFace)
          resolve()
        }).catch(reject)
      } else {
        // 兼容性处理
        setTimeout(resolve, 1000)
      }
    })
  }

  // 获取水印的 OSSURL
  getRemarkUrl (imgUrl, questionIndex) {
    // 生成8位随机数
    const randomNum = Math.floor(Math.random() * 90000000) + 10000000
    // 获取时间戳 (type=1 返回时间戳)
    const timestamp = this.getWaterMarkTime(1)
    // 使用 imgUrl 作为基础URL，取 / 后面第一个 _ 前面的内容
    const lastSlashIndex = imgUrl.lastIndexOf('/')
    const afterSlash = imgUrl.substring(lastSlashIndex + 1)
    const firstUnderscoreIndex = afterSlash.indexOf('_')
    const baseUrl = imgUrl.substring(0, lastSlashIndex + 1) + afterSlash.substring(0, firstUnderscoreIndex > 0 ? firstUnderscoreIndex : afterSlash.length)
    const fileName = `_${questionIndex + 1}_first_normal_${timestamp}_${randomNum}.jpeg`
    return baseUrl + fileName
  }
  // 获取视频的 OSSURL
  getVideoRemarkUrl (url, questionIndex, duration) {
    // 生成8位随机数
    const randomNum = Math.floor(Math.random() * 90000000) + 10000000
    // 获取时间戳 (type=1 返回时间戳)
    const timestamp = this.getWaterMarkTime(1)
    // 使用 url 作为基础URL，取 / 后面第一个 _ 前面的内容
    const lastSlashIndex = url.lastIndexOf('/')
    const afterSlash = url.substring(lastSlashIndex + 1)
    const firstUnderscoreIndex = afterSlash.indexOf('_')
    const baseUrl = url.substring(0, lastSlashIndex + 1) + afterSlash.substring(0, firstUnderscoreIndex > 0 ? firstUnderscoreIndex : afterSlash.length)
    const fileName = `_${questionIndex + 1}_first_normal_dur${duration}_${timestamp}_${randomNum}.mp4`
    return baseUrl + fileName
  }
}

// 导出类，供需要创建多个实例的场景使用
export default ImgAddMark



// WEBPACK FOOTER //
// ./src/views/question-review/utils/ImgAddMark.js